import React, {useState, useEffect} from "react"
import Mustache from "mustache"
import Editor from "@monaco-editor/react"
import Button from "../Button/Button"
import Icon from "../Icon/Icon"
import {getNestedValue, setNestedValue} from "../../utilities/objects"
import classNames from "classnames"

// Sample colors from our color palette
const UI_COLORS = {
	error: "#FF506F", // $secondary-red
	neutral: "#808285", // $neutrals-dark
	primary: "#30E87A", // $main-green
	success: "#2E75FF", // $secondary-blue
}

const securityWarning = "Security Warning: <script> tags are not allowed in templates."

interface MustacheEditorProps {
	initialHtml: string
	onSave: (html: string, subject?: string, samples?: string) => void
	defaultVariables?: Record<string, any>
	initialSubject?: string
	showSubjectField?: boolean
	samples?: string
}

type ViewMode = "preview-only" | "side-by-side" | "edit-only"

interface ArrayVariable {
	name: string
	properties: string[]
	rows: Record<string, string>[]
}

export default function MustacheEditor({
	initialHtml,
	onSave,
	defaultVariables = {},
	initialSubject = "",
	showSubjectField = false,
	samples,
}: MustacheEditorProps) {
	const [initialized, setInitialized] = useState(false)
	const [htmlContent, setHtmlContent] = useState(initialHtml)
	const [variablesObject, setVariablesObject] = useState<Record<string, any>>(defaultVariables)
	const [renderedHtml, setRenderedHtml] = useState("")
	const [previewError, setPreviewError] = useState<string | null>(null)
	const [viewMode, setViewMode] = useState<ViewMode>("side-by-side")
	const [showRawJson, setShowRawJson] = useState(false)
	const [variablesJson, setVariablesJson] = useState(JSON.stringify(defaultVariables, null, 2))
	const [hasScriptTags, setHasScriptTags] = useState(false)
	const [subject, setSubject] = useState(initialSubject)
	const [arrayVariables, setArrayVariables] = useState<ArrayVariable[]>([])
	const [detectedVariables, setDetectedVariables] = useState<string[]>([])
	const [conditionalVariables, setConditionalVariables] = useState<string[]>([])
	const [flattenedVariables, setFlattenedVariables] = useState<Record<string, string>>({})
	const [arrayAsBooleanMap, setArrayAsBooleanMap] = useState<Record<string, boolean>>({})

	// Validate HTML for script tags
	const validateHtml = (html: string): boolean => {
		const scriptTagRegex = /<script[\s\S]*?>[\s\S]*?<\/script>|<script[\s\S]*?\/>/gi
		return !scriptTagRegex.test(html)
	}

	// Extract Mustache variables from the template without updating state
	const extractVariables = (html: string): {variables: string[]; arrays: ArrayVariable[]; conditionals: string[]} => {
		// Holder for all the variables we find
		const allVariables: string[] = []
		const sections: Record<string, Set<string>> = {}
		const definiteConditionals: Set<string> = new Set()
		const positiveCondVars: Set<string> = new Set()
		const negativeCondVars: Set<string> = new Set()
		const allConditionals: Set<string> = new Set()

		// STEP 1: Find all variables used in conditional contexts (both positive and negative)
		const findConditionalVariables = () => {
			const conditionalsPattern = /{{[#^]([a-zA-Z0-9_]+)}}/g
			let match
			while ((match = conditionalsPattern.exec(html)) !== null) {
				const [fullMatch, varName] = match
				if (fullMatch.includes("{{#")) {
					positiveCondVars.add(varName)
				}
				if (fullMatch.includes("{{^")) {
					negativeCondVars.add(varName)
				}
				allConditionals.add(varName)

				// If a variable appears in both positive and negative conditionals, it's definitely a conditional
				if (positiveCondVars.has(varName) && negativeCondVars.has(varName)) {
					definiteConditionals.add(varName)
				}
			}
		}

		// STEP 2: Find sections that contain their own name as variables (common pattern for conditionals)
		const findSelfReferencingSections = () => {
			const selfRefPattern = /{{[#^]([a-zA-Z0-9_]+)}}[.\s\S]*?{{([a-zA-Z0-9_]+)}}[.\s\S]*?{{\/\1}}/g
			let match
			while ((match = selfRefPattern.exec(html)) !== null) {
				const [, sectionName, innerVariable] = match
				if (sectionName === innerVariable) {
					definiteConditionals.add(sectionName)
				}
			}
		}

		// STEP 3: Helper to check if a variable appears in specific contexts
		const isUsedInBothContexts = (name: string): boolean => positiveCondVars.has(name) && negativeCondVars.has(name)

		const isComplexConditional = (name: string): boolean =>
			html.includes(`{{#${name}}}`) && html.includes(`{{^${name}}}`)

		const isSelfReferential = (name: string): boolean => html.includes(`{{#${name}}}`) && html.includes(`{{${name}}}`)

		// STEP 4: Process section content recursively
		const processSectionContent = (html: string, sectionName: string, isNegative = false, depth = 0): void => {
			const startTag = isNegative ? `{{^${sectionName}}}` : `{{#${sectionName}}}`
			const endTag = `{{/${sectionName}}}`
			const startPos = html.indexOf(startTag)

			if (startPos === -1) return

			// Find the content between the start tag and end tag
			const contentStart = startPos + startTag.length
			let openTags = 1
			let currentPos = contentStart
			let sectionContent = ""

			// Handle nested section tags properly
			while (openTags > 0 && currentPos < html.length) {
				const nextOpenPosHash = html.indexOf(`{{#${sectionName}}}`, currentPos)
				const nextOpenPosCaret = html.indexOf(`{{^${sectionName}}}`, currentPos)
				let nextOpenTagPos = -1

				// Find the closest opening tag (either positive or negative)
				if (nextOpenPosHash !== -1 && nextOpenPosCaret !== -1) {
					nextOpenTagPos = Math.min(nextOpenPosHash, nextOpenPosCaret)
				} else if (nextOpenPosHash !== -1) {
					nextOpenTagPos = nextOpenPosHash
				} else if (nextOpenPosCaret !== -1) {
					nextOpenTagPos = nextOpenPosCaret
				}

				const nextCloseTagPos = html.indexOf(endTag, currentPos)

				if (nextCloseTagPos === -1) {
					// End tag missing, use whatever we've got
					sectionContent = html.substring(contentStart)
					break
				}

				if (nextOpenTagPos !== -1 && nextOpenTagPos < nextCloseTagPos) {
					// Found another opening tag before the closing tag
					openTags++
					currentPos = nextOpenTagPos + startTag.length
				} else {
					// Found a closing tag
					openTags--
					if (openTags === 0) {
						sectionContent = html.substring(contentStart, nextCloseTagPos)
						break
					}
					currentPos = nextCloseTagPos + endTag.length
				}
			}

			// If failed to find with nested handling, try simple extraction
			if (!sectionContent) {
				const simpleEndPos = html.indexOf(endTag, contentStart)
				if (simpleEndPos !== -1) {
					sectionContent = html.substring(contentStart, simpleEndPos)
				}
			}

			// Process the content of this section
			if (sectionContent) {
				processTokens(Mustache.parse(sectionContent), sectionName, depth)
			}
		}

		// STEP 5: Process tokens from Mustache.parse
		const processTokens = (tokens: any[], parentSection?: string, depth = 0) => {
			tokens.forEach((token) => {
				const [type, name] = token

				if (type === "name" || type === "&") {
					// Regular variable
					allVariables.push(name)

					// If inside a section, add to section's properties (unless it's the section's own name)
					if (parentSection && name !== parentSection) {
						sections[parentSection].add(name)
					}
				} else if (type === "#" || type === "^") {
					// Found a section/conditional
					allConditionals.add(name)

					// Track positive/negative usage
					if (type === "#") {
						positiveCondVars.add(name)
					} else {
						negativeCondVars.add(name)
					}

					// If used in both contexts, mark as definite conditional
					if (positiveCondVars.has(name) && negativeCondVars.has(name)) {
						definiteConditionals.add(name)
					}

					// Initialize section properties set if needed
					if (!sections[name]) {
						sections[name] = new Set()
					}

					// Recursively process this section's content (limit depth to prevent stack overflow)
					if (depth < 10) {
						processSectionContent(html, name, type === "^", depth + 1)
					}
				}
			})
		}

		// STEP 6: Classify variables into different types
		const classifyVariables = () => {
			// Identify obvious conditionals first
			const conditionalVars = Array.from(allConditionals).filter((name) => {
				// Already identified as a definite conditional
				if (definiteConditionals.has(name)) {
					return true
				}

				// Check if it has substantial properties (likely an array then)
				const hasSubstantialProperties = sections[name] && sections[name].size > 2
				if (hasSubstantialProperties) {
					return false
				}

				// Used in both positive and negative contexts
				if (isUsedInBothContexts(name)) {
					return true
				}

				// Complex conditional usage patterns
				if (isComplexConditional(name) || isSelfReferential(name)) {
					return true
				}

				// If it has no or few properties, or used as a variable itself
				return (
					!sections[name] || // No inner variables
					sections[name].size === 0 || // Empty section
					(sections[name].size <= 2 && allVariables.includes(name)) // Few properties and used as variable
				)
			})

			// Identify array variables (must have properties and not be identified as conditional)
			const arrayVars = Object.entries(sections)
				.filter(([name, props]) => {
					// Skip if already classified as a conditional
					if (conditionalVars.includes(name)) {
						return false
					}

					// If has substantial properties, likely an array
					if (props.size > 2) {
						return true
					}

					// Skip any definite conditionals
					if (
						definiteConditionals.has(name) ||
						isUsedInBothContexts(name) ||
						isComplexConditional(name) ||
						isSelfReferential(name)
					) {
						return false
					}

					// Must have properties and not be a simple conditional used as a variable
					const isArray = props.size > 0
					const isUsedAsVariable = allVariables.includes(name)
					const isSimpleConditional = props.size <= 2 && isUsedAsVariable
					return isArray && !isSimpleConditional
				})
				.map(([name, props]) => {
					// Create array variable definition
					const properties = Array.from(props).sort()

					// Check for existing data or create sample data
					const existingArray = variablesObject[name]
					let rows: Record<string, string>[] = [{}]

					if (existingArray && Array.isArray(existingArray) && existingArray.length > 0) {
						rows = existingArray
					} else {
						// Create sample data
						rows = [
							properties.reduce((obj, prop) => {
								obj[prop] = getSmartSampleValue(prop)
								return obj
							}, {} as Record<string, string>),
							properties.reduce((obj, prop) => {
								obj[prop] = getSmartSampleValue(prop) + " (2)"
								return obj
							}, {} as Record<string, string>),
						]
					}

					return {name, properties, rows}
				})

			// Get all properties that belong to array variables
			const arrayProperties = new Set<string>()
			arrayVars.forEach(({properties}) => {
				properties.forEach((prop) => arrayProperties.add(prop))
			})

			// Regular variables (not conditionals and not array properties)
			const regularVars = allVariables
				.filter((value, index, self) => self.indexOf(value) === index) // Deduplicate
				.filter((v) => !conditionalVars.includes(v)) // Not conditionals
				.filter((v) => !arrayProperties.has(v)) // Not array properties
				.sort()

			return {
				variables: regularVars,
				arrays: arrayVars,
				conditionals: conditionalVars,
			}
		}

		// Execute the parsing pipeline
		findConditionalVariables()
		findSelfReferencingSections()
		processTokens(Mustache.parse(html)) // Process top-level tokens
		return classifyVariables()
	}

	// Extract variables and update state in an effect when content changes
	useEffect(() => {
		let variables: string[] = []
		let arrays: ArrayVariable[] = []
		let conditionals: string[] = []
		try {
			const result = extractVariables(htmlContent)
			variables = result.variables
			arrays = result.arrays
			conditionals = result.conditionals
		} catch (e) {
			console.error("Error extracting variables:", e)
			return
		}

		// Debug logging to see how sections are classified
		console.log("Detected variables:", variables)
		console.log(
			"Detected arrays:",
			arrays.map((a) => a.name)
		)
		console.log("Detected conditionals:", conditionals)

		// Make sure array rows use the current variablesObject data
		const updatedArrays = arrays.map((arrayVar) => {
			// If we already have data for this array in variablesObject, use it
			const existingArray = variablesObject[arrayVar.name]
			if (existingArray && Array.isArray(existingArray) && existingArray.length > 0) {
				return {
					...arrayVar,
					rows: existingArray,
				}
			}
			return arrayVar
		})

		setDetectedVariables(variables)
		setFlattenedVariables(flattenVariables(variables))
		setArrayVariables(updatedArrays)
		setConditionalVariables(conditionals)
	}, [htmlContent, variablesObject])

	// Get smart sample value for a variable based on its name
	const getSmartSampleValue = (variable: string): string => {
		// First check if we have samples and use the first sample's value
		try {
			if (samples) {
				const parsedSamples = JSON.parse(samples)
				if (Array.isArray(parsedSamples) && parsedSamples.length > 0 && parsedSamples[0][variable]) {
					return parsedSamples[0][variable]
				}
			}
		} catch (e) {
			console.warn("Failed to parse samples JSON:", e)
		}

		if (variable.includes("firstName")) {
			return "John"
		} else if (variable.includes("lastName")) {
			return "Doe"
		} else if (
			variable.toLowerCase().includes("name") &&
			!variable.toLowerCase().includes("first") &&
			!variable.toLowerCase().includes("last")
		) {
			return "John Doe"
		} else if (variable.includes("email")) {
			return "john.doe@example.com"
		} else if (variable.includes("primaryColor")) {
			return UI_COLORS.primary
		} else if (variable.includes("successColor")) {
			return UI_COLORS.success
		} else if (variable.includes("errorColor")) {
			return UI_COLORS.error
		} else if (variable.includes("company")) {
			return "Relay"
		} else if (variable.includes("brandName")) {
			return "Relay"
		} else if (variable.includes("brandLogoUrl")) {
			return "https://cdn.prod.website-files.com/67ab6f3014e9ebd84ecef25a/67ab6f3014e9ebd84ecef291_Unit%20Logo.svg"
		} else if (variable.includes("date") || variable.toLowerCase().includes("createdat")) {
			return new Date().toLocaleDateString()
		} else if (variable.includes("address")) {
			return "123 Main St, New York, NY"
		} else if (variable.toLowerCase().includes("phone")) {
			return "(555) 123-4567"
		} else if (
			variable.toLowerCase().includes("url") ||
			variable.toLowerCase().includes("website") ||
			variable.toLowerCase().includes("link")
		) {
			return "https://relayfi.com"
		} else if (variable.toLowerCase().includes("support")) {
			return "support@relayfi.com"
		} else if (variable.includes("amount")) {
			return "123.45"
		} else if (variable.includes("merchant")) {
			return "Acme Corp"
		}

		return `Sample ${variable}`
	}

	// Create a flattened object from the variable paths
	const flattenVariables = (variables: string[]) => {
		const result: Record<string, string> = {}

		variables.forEach((variable) => {
			// Check if value already exists in variables object
			const existingValue = getNestedValue(variablesObject, variable)

			if (existingValue !== undefined) {
				result[variable] = existingValue
			} else {
				result[variable] = getSmartSampleValue(variable)
			}
		})

		return result
	}

	const createArrayDisplayMode = (arrayAsBooleanMap: Record<string, boolean>) =>
		Object.keys(arrayAsBooleanMap)
			.filter((key) => arrayAsBooleanMap[key])
			.reduce((obj, key) => {
				obj[key] = "boolean"
				return obj
			}, {} as Record<string, string>)

	// Update JSON from variables object
	useEffect(() => {
		if (showRawJson) {
			try {
				const parsedVariables = JSON.parse(variablesJson)
				setVariablesObject(parsedVariables)

				// When loading from JSON, detect any boolean values for array variables
				// and update the arrayAsBooleanMap accordingly
				const newArrayAsBooleanMap = {...arrayAsBooleanMap}

				arrayVariables.forEach((arrayVar) => {
					const value = parsedVariables[arrayVar.name]
					if (typeof value === "boolean") {
						newArrayAsBooleanMap[arrayVar.name] = true
					} else if (Array.isArray(value) && newArrayAsBooleanMap[arrayVar.name]) {
						// If it's an array now but was previously a boolean, update the map
						newArrayAsBooleanMap[arrayVar.name] = false
					}
				})

				setArrayAsBooleanMap(newArrayAsBooleanMap)
			} catch (e) {
				// Don't update if JSON is invalid
			}
		} else {
			// Create an enhanced variables object that includes metadata
			const enhancedVariables = {
				...variablesObject,
				__meta: {
					arrayDisplayMode: createArrayDisplayMode(arrayAsBooleanMap),
				},
			}

			setVariablesJson(JSON.stringify(enhancedVariables, null, 2))
		}
	}, [variablesObject, showRawJson, arrayAsBooleanMap, arrayVariables])

	// Initialize variables with sample values on first render only
	useEffect(() => {
		// Extract variables and detect arrays from the initial HTML
		const {
			variables: initialVariables,
			arrays: initialArrays,
			conditionals: initialConditionals,
		} = extractVariables(initialHtml)

		console.log("Initializing variables:", initialVariables)
		console.log("Initializing conditionals:", initialConditionals)

		// Start with default variables, then overlay with samples if available
		const initialVariablesObject = {...defaultVariables}

		// If we have samples, merge the first sample into the initial variables
		try {
			if (samples) {
				const parsedSamples = JSON.parse(samples)
				if (Array.isArray(parsedSamples) && parsedSamples.length > 0) {
					Object.assign(initialVariablesObject, parsedSamples[0])
				}
			}
		} catch (e) {
			console.warn("Failed to parse samples JSON during initialization:", e)
		}

		const initialArrayAsBooleanMap: Record<string, boolean> = {}

		// Check if there's metadata in the default variables or samples
		let metaSource = defaultVariables.__meta
		try {
			if (samples && !metaSource) {
				const parsedSamples = JSON.parse(samples)
				if (Array.isArray(parsedSamples) && parsedSamples.length > 0) {
					metaSource = parsedSamples[0].__meta
				}
			}
		} catch (e) {
			console.warn("Failed to parse samples for metadata:", e)
		}

		if (metaSource && metaSource.arrayDisplayMode) {
			// Load array display preferences from metadata
			Object.entries(metaSource.arrayDisplayMode).forEach(([key, mode]) => {
				if (mode === "boolean") {
					initialArrayAsBooleanMap[key] = true
				}
			})

			// Remove metadata from the variables object to avoid rendering issues
			delete initialVariablesObject.__meta
		}

		// Initialize regular variables
		initialVariables.forEach((variable) => {
			if (getNestedValue(initialVariablesObject, variable) === undefined) {
				Object.assign(
					initialVariablesObject,
					setNestedValue(initialVariablesObject, variable, getSmartSampleValue(variable))
				)
			}
		})

		// Initialize conditional variables with smart sample values instead of false by default
		initialConditionals.forEach((conditional) => {
			if (getNestedValue(initialVariablesObject, conditional) === undefined) {
				Object.assign(
					initialVariablesObject,
					setNestedValue(initialVariablesObject, conditional, getSmartSampleValue(conditional))
				)
			}
		})

		// Initialize array variables with sample data
		initialArrays.forEach((arrayVar) => {
			// Check if this array should be displayed as a boolean based on metadata
			if (initialArrayAsBooleanMap[arrayVar.name]) {
				// If it's marked as boolean but doesn't have a boolean value, initialize it
				if (typeof initialVariablesObject[arrayVar.name] !== "boolean") {
					initialVariablesObject[arrayVar.name] = true // Default to true for better preview
				}
			}
			// Check if the existing value is a boolean - if so, initialize it as a boolean display
			else if (typeof initialVariablesObject[arrayVar.name] === "boolean") {
				initialArrayAsBooleanMap[arrayVar.name] = true
			} else if (!initialVariablesObject[arrayVar.name] || !Array.isArray(initialVariablesObject[arrayVar.name])) {
				// Create sample array with 2 items for better preview
				const sampleArray = [
					arrayVar.properties.reduce((obj, prop) => {
						obj[prop] = getSmartSampleValue(prop)
						return obj
					}, {} as Record<string, string>),
					arrayVar.properties.reduce((obj, prop) => {
						// Slightly different values for the second item
						obj[prop] = getSmartSampleValue(prop) + " (2)"
						return obj
					}, {} as Record<string, string>),
				]

				initialVariablesObject[arrayVar.name] = sampleArray
			}
		})

		// Set the variables object with the sample values
		setVariablesObject(initialVariablesObject)
		setVariablesJson(JSON.stringify(initialVariablesObject, null, 2))
		setDetectedVariables(initialVariables)
		setFlattenedVariables(flattenVariables(initialVariables))
		setArrayVariables(initialArrays)
		setConditionalVariables(initialConditionals)
		setArrayAsBooleanMap(initialArrayAsBooleanMap)

		// Set content and mark initialization as complete
		setHtmlContent(initialHtml)
		setInitialized(true)
	}, [])

	// Auto-extract variables when HTML content changes
	useEffect(() => {
		if (!initialized) return

		// We already extract and process variables in another effect,
		// so we just need to look for new ones here
		const updatedVariables = {...variablesObject}
		let hasNewVariables = false

		// Check if there are any new variables that don't exist yet
		detectedVariables.forEach((variable) => {
			if (getNestedValue(updatedVariables, variable) === undefined) {
				// Only add new variables that don't exist yet
				const newObj = setNestedValue(updatedVariables, variable, getSmartSampleValue(variable))
				Object.assign(updatedVariables, newObj)
				hasNewVariables = true
			}
		})

		// Check for new conditional variables - use smart sample values instead of false
		conditionalVariables.forEach((conditional) => {
			if (getNestedValue(updatedVariables, conditional) === undefined) {
				const newObj = setNestedValue(updatedVariables, conditional, getSmartSampleValue(conditional))
				Object.assign(updatedVariables, newObj)
				hasNewVariables = true
			}
		})

		// Only update if we found new variables
		if (hasNewVariables) {
			setVariablesObject(updatedVariables)
		}
	}, [initialized, detectedVariables, conditionalVariables])

	// Render template when initialized or variables change
	useEffect(() => {
		if (!initialized) return

		// Check if content has script tags
		if (hasScriptTags) {
			setPreviewError(securityWarning)
			setRenderedHtml(
				`<div style="padding: 20px; color: ${UI_COLORS.error};">Preview blocked due to security concerns. Please remove all script tags.</div>`
			)
			return
		}

		try {
			// Create a processed version of variables for rendering
			const processedVariables = {...variablesObject}

			// Process array variables that are now booleans
			Object.keys(arrayAsBooleanMap).forEach((arrayName) => {
				if (arrayAsBooleanMap[arrayName]) {
					// If an array is being handled as a boolean, ensure it has the right data type
					processedVariables[arrayName] = Boolean(processedVariables[arrayName])
				}
			})

			// Render the template with processed variables
			const rendered = Mustache.render(htmlContent, processedVariables)

			// Double-check the rendered output for script tags that might have been inserted through variables
			if (!validateHtml(rendered)) {
				setPreviewError(securityWarning)
				setRenderedHtml(
					`<div style="padding: 20px; color: ${UI_COLORS.error};">Preview blocked due to security concerns. Please check your variables for script content.</div>`
				)
				return
			}

			setRenderedHtml(rendered)
			setPreviewError(null)
		} catch (error) {
			console.error("Render failed", error)
			setPreviewError(`Mustache rendering error: ${error instanceof Error ? error.message : String(error)}`)
		}
	}, [initialized, variablesObject, htmlContent, hasScriptTags, arrayAsBooleanMap])

	const handleHtmlChange = (value: string | undefined) => {
		const newContent = value || ""
		setHtmlContent(newContent)

		// Check for script tags whenever HTML content changes
		const isValid = validateHtml(newContent)
		setHasScriptTags(!isValid)

		// If script tags are detected, set an error
		if (!isValid) {
			setPreviewError(securityWarning)
		} else if (previewError === securityWarning) {
			// Only clear the error if it was a script tag error
			setPreviewError(null)
		}
	}

	const handleVariableChange = (variable: string, value: string) => {
		const updatedVariables = setNestedValue({...variablesObject}, variable, value)
		setVariablesObject(updatedVariables)
	}

	const handleRawJsonChange = (value: string | undefined) => {
		setVariablesJson(value || "{}")

		// Try to parse and update variables object if JSON is valid
		try {
			const parsedVariables = JSON.parse(value || "{}")
			setVariablesObject(parsedVariables)
		} catch (e) {
			// Don't update variables if JSON is invalid
			setPreviewError(`Invalid JSON: ${e instanceof Error ? e.message : String(e)}`)
		}
	}

	const handleSave = () => {
		// Prevent saving if HTML contains script tags
		if (hasScriptTags) {
			setPreviewError("Cannot save template with <script> tags. Please remove them first.")
			return
		}

		// Create a clean version of the variables object without internal metadata
		const cleanVariables = {...variablesObject}

		// Remove any existing metadata if present
		if (cleanVariables.__meta) {
			delete cleanVariables.__meta
		}

		// Add metadata about array display preferences
		const hasArrayBooleanSettings = Object.keys(arrayAsBooleanMap).some((key) => arrayAsBooleanMap[key])

		if (hasArrayBooleanSettings) {
			cleanVariables.__meta = {
				arrayDisplayMode: createArrayDisplayMode(arrayAsBooleanMap),
			}
		}

		// Create samples array from current template variables (single item array)
		const currentSamples = [cleanVariables]

		// Convert to JSON string
		const samplesJson = JSON.stringify(currentSamples)

		// Update the stored JSON with the clean version including metadata
		setVariablesJson(JSON.stringify(cleanVariables, null, 2))

		if (showSubjectField) {
			onSave(htmlContent, subject, samplesJson)
		} else {
			onSave(htmlContent, undefined, samplesJson)
		}
	}

	const toggleRawJson = () => {
		setShowRawJson(!showRawJson)
	}

	// Check initial HTML for script tags when component mounts
	useEffect(() => {
		const isValid = validateHtml(initialHtml)
		setHasScriptTags(!isValid)

		if (!isValid) {
			setPreviewError(securityWarning)
		}
	}, [initialHtml])

	const handleArrayRowChange = (arrayName: string, rowIndex: number, property: string, value: string) => {
		const updatedArrays = arrayVariables.map((arr) => {
			if (arr.name === arrayName) {
				const newRows = [...arr.rows]
				newRows[rowIndex] = {...newRows[rowIndex], [property]: value}
				return {...arr, rows: newRows}
			}
			return arr
		})
		setArrayVariables(updatedArrays)

		// Update the main variables object
		const updatedVariables = {...variablesObject}
		updatedVariables[arrayName] = updatedArrays.find((arr) => arr.name === arrayName)?.rows || []
		setVariablesObject(updatedVariables)
	}

	const handleAddRow = (arrayName: string) => {
		const updatedArrays = arrayVariables.map((arr) => {
			if (arr.name === arrayName) {
				const newRow = arr.properties.reduce((acc, prop) => ({...acc, [prop]: ""}), {})
				return {...arr, rows: [...arr.rows, newRow]}
			}
			return arr
		})
		setArrayVariables(updatedArrays)

		// Update the main variables object
		const updatedVariables = {...variablesObject}
		updatedVariables[arrayName] = updatedArrays.find((arr) => arr.name === arrayName)?.rows || []
		setVariablesObject(updatedVariables)
	}

	const handleRemoveRow = (arrayName: string, rowIndex: number) => {
		const updatedArrays = arrayVariables.map((arr) => {
			if (arr.name === arrayName) {
				const newRows = arr.rows.filter((_, index) => index !== rowIndex)
				return {...arr, rows: newRows.length > 0 ? newRows : [{}]} // Keep at least one empty row
			}
			return arr
		})
		setArrayVariables(updatedArrays)

		// Update the main variables object
		const updatedVariables = {...variablesObject}
		updatedVariables[arrayName] = updatedArrays.find((arr) => arr.name === arrayName)?.rows || []
		setVariablesObject(updatedVariables)
	}

	// Toggle between array and boolean representation
	const toggleArrayAsBoolean = (arrayName: string) => {
		const newArrayAsBooleanMap = {...arrayAsBooleanMap}
		newArrayAsBooleanMap[arrayName] = !arrayAsBooleanMap[arrayName]
		setArrayAsBooleanMap(newArrayAsBooleanMap)

		// Update variable value accordingly
		const updatedVariables = {...variablesObject}

		if (newArrayAsBooleanMap[arrayName]) {
			// When converting to boolean, set to true by default
			updatedVariables[arrayName] = true
		} else {
			// When converting back to array, restore array structure
			const arrayVar = arrayVariables.find((arr) => arr.name === arrayName)
			if (arrayVar) {
				updatedVariables[arrayName] = arrayVar.rows.length > 0 ? arrayVar.rows : [{}]
			}
		}

		setVariablesObject(updatedVariables)
	}

	// Handle boolean change for array variables treated as booleans
	const handleBooleanChange = (arrayName: string, value: boolean) => {
		const updatedVariables = {...variablesObject}
		updatedVariables[arrayName] = value
		setVariablesObject(updatedVariables)
	}

	const renderArrayTable = (arrayVar: ArrayVariable) => {
		// Check if this array should be rendered as a boolean
		if (arrayAsBooleanMap[arrayVar.name]) {
			return (
				<div key={arrayVar.name} className="array-variable-boolean">
					<div className="array-variable-header">
						<h4>{arrayVar.name}</h4>
						<div className="array-controls">
							<label className="toggle-display-mode">
								<span>Display as: Conditional</span>
								<Button onClick={() => toggleArrayAsBoolean(arrayVar.name)} theme="neutral" size="small">
									Switch to List
								</Button>
							</label>
						</div>
					</div>
					<div className="boolean-control">
						<label className="checkbox-container">
							<input
								type="checkbox"
								checked={Boolean(variablesObject[arrayVar.name])}
								onChange={(e) => handleBooleanChange(arrayVar.name, e.target.checked)}
							/>
							<span>Enable {arrayVar.name}</span>
						</label>
						<div className="boolean-description">
							<small>When checked, this section will be displayed in the template.</small>
						</div>
					</div>
				</div>
			)
		}

		// Original array table rendering
		return (
			<div key={arrayVar.name} className="array-variable-table">
				<div className="array-variable-header">
					<h4>{arrayVar.name}</h4>
					<div className="array-controls">
						<label className="toggle-display-mode">
							<span>Display as: List</span>
							<Button onClick={() => toggleArrayAsBoolean(arrayVar.name)} theme="neutral" size="small">
								Switch to Conditional
							</Button>
						</label>
						<Button onClick={() => handleAddRow(arrayVar.name)} theme="success" size="small">
							<Icon icon="alert-circle--interface-essential" size={12} /> Add Row
						</Button>
					</div>
				</div>
				<div className="array-table-container">
					<table>
						<thead>
							<tr>
								{arrayVar.properties.map((prop) => (
									<th key={prop}>{prop}</th>
								))}
								<th className="actions-column">Actions</th>
							</tr>
						</thead>
						<tbody>
							{arrayVar.rows.map((row, rowIndex) => (
								<tr key={rowIndex}>
									{arrayVar.properties.map((prop) => (
										<td key={prop}>
											<input
												type="text"
												value={row[prop] || ""}
												onChange={(e) => handleArrayRowChange(arrayVar.name, rowIndex, prop, e.target.value)}
												placeholder={`Value for ${prop}`}
											/>
										</td>
									))}
									<td className="actions-column">
										<button
											className="remove-row-btn"
											onClick={() => handleRemoveRow(arrayVar.name, rowIndex)}
											disabled={arrayVar.rows.length === 1}
											title={arrayVar.rows.length === 1 ? "Cannot remove last row" : "Remove row"}
										>
											<Icon icon="delete-close-x" size={14} />
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		)
	}

	// Handler for conditional variable changes - updated to handle text input
	const handleConditionalChange = (conditional: string, value: string) => {
		// If input is empty, set to boolean false
		// Otherwise use the entered value
		const newValue = value.trim() === "" ? false : value
		const updatedVariables = setNestedValue({...variablesObject}, conditional, newValue)
		setVariablesObject(updatedVariables)
	}

	return (
		<div className="mustache-editor">
			<div className="mustache-editor-controls">
				<div className="view-switcher">
					<button
						className={classNames("view-btn", viewMode === "edit-only" && "active")}
						onClick={() => setViewMode("edit-only")}
						title="Edit Only"
					>
						<Icon icon="pencil-1--interface-essential" size={16} />
						<span>Edit</span>
					</button>
					<button
						className={classNames("view-btn", viewMode === "side-by-side" && "active")}
						onClick={() => setViewMode("side-by-side")}
						title="Side by Side"
					>
						<Icon icon="module-three--interface-essential" size={16} />
						<span>Split</span>
					</button>
					<button
						className={classNames("view-btn", viewMode === "preview-only" && "active")}
						onClick={() => setViewMode("preview-only")}
						title="Preview Only"
					>
						<Icon icon="document-paragraph-text-image-1--interface-essential" size={16} />
						<span>Preview</span>
					</button>
				</div>

				<div className="center-controls">
					{showSubjectField && (
						<div className="subject-field">
							<label htmlFor="template-subject">Subject:</label>
							<input
								type="text"
								id="template-subject"
								value={subject}
								onChange={(e) => setSubject(e.target.value)}
								placeholder="Email subject"
								style={{width: "300%", maxWidth: "600px"}}
							/>
						</div>
					)}
				</div>

				<Button
					onClick={handleSave}
					theme="success"
					disabled={hasScriptTags}
					title={hasScriptTags ? "Cannot save templates with script tags" : "Save template"}
				>
					Save New Template
				</Button>
			</div>

			{hasScriptTags && (
				<div className="security-warning">
					<Icon icon="alert-circle--interface-essential" size={16} />
					<span>{securityWarning}</span>
				</div>
			)}

			<div className={classNames("mustache-editor-layout", viewMode)}>
				{viewMode !== "preview-only" && (
					<div className="mustache-editor-edit-panel">
						<div className="mustache-editor-panel html-editor">
							<div className="panel-title">
								<span>HTML Template</span>
							</div>
							<div className="editor-container">
								<Editor
									height="calc(100% - 10px)"
									language="html"
									value={htmlContent}
									onChange={handleHtmlChange}
									options={{
										minimap: {enabled: false},
										lineNumbers: "on",
										scrollBeyondLastLine: false,
										wordWrap: "on",
									}}
								/>
							</div>
						</div>

						<div className="mustache-editor-panel variables-editor">
							<div className="panel-title-with-action">
								<span>Template Variables</span>
								<div className="panel-actions">
									<button
										className="toggle-json-btn"
										onClick={toggleRawJson}
										title={showRawJson ? "Show Form Fields" : "Show Raw JSON"}
									>
										{showRawJson ? "Form View" : "JSON View"}
									</button>
								</div>
							</div>

							<div className="editor-container">
								{showRawJson ? (
									<Editor
										height="calc(100% - 10px)"
										language="json"
										value={variablesJson}
										onChange={handleRawJsonChange}
										options={{
											minimap: {enabled: false},
											lineNumbers: "on",
											scrollBeyondLastLine: false,
											wordWrap: "on",
										}}
									/>
								) : (
									<div className="variables-form">
										{/* Array Variables Tables */}
										{arrayVariables.map((arrayVar) => renderArrayTable(arrayVar))}

										{/* Conditional Variables - Simple UI */}
										{conditionalVariables.length > 0 && (
											<div>
												<div style={{marginBottom: "10px", borderBottom: "1px solid #ddd", paddingBottom: "5px"}}>
													<strong>Conditional Sections</strong>
												</div>
												{conditionalVariables.map((conditional) => {
													// Check if this conditional is nested within another conditional
													const isNested =
														htmlContent.includes(`{{#`) &&
														htmlContent.includes(`{{#${conditional}}}`) &&
														!htmlContent.match(new RegExp(`^[^{]*{{#${conditional}}}`))

													return (
														<div key={conditional} className="variable-field" style={{marginBottom: "8px"}}>
															<label title={`Section: {{#${conditional}}}`}>
																{isNested && (
																	<span style={{marginRight: "4px", color: "#888", fontSize: "14px"}}>↳</span>
																)}
																{conditional}
															</label>
															<input
																type="text"
																value={String(getNestedValue(variablesObject, conditional) || "")}
																onChange={(e) => handleConditionalChange(conditional, e.target.value)}
																placeholder={`Value for conditional ${conditional}`}
															/>
														</div>
													)
												})}
											</div>
										)}

										{/* Regular Variables */}
										{Object.entries(flattenedVariables).length > 0 ? (
											Object.entries(flattenedVariables).map(([variable, value]) => (
												<div key={variable} className="variable-field">
													<label title={variable}>{variable}</label>
													<input
														type="text"
														value={String(value)}
														onChange={(e) => handleVariableChange(variable, e.target.value)}
														placeholder={`Value for ${variable}`}
													/>
												</div>
											))
										) : (
											<div className="no-variables-message">
												No variables detected in template.
												<br />
												Add variables using double curly braces: <code>{"{{variable}}"}</code>
											</div>
										)}
									</div>
								)}
								{previewError && <div className="error-message">{previewError}</div>}
							</div>
						</div>
					</div>
				)}

				{viewMode !== "edit-only" && (
					<div className="mustache-editor-preview-panel">
						<div className="mustache-editor-preview">
							<h3 className="panel-title">Live Preview</h3>
							{previewError ? (
								<div className="error-message">{previewError}</div>
							) : (
								<div className="preview-container">
									<iframe
										key={`preview-${renderedHtml.length}`}
										title="Mustache Preview"
										srcDoc={
											renderedHtml ||
											`<div style="padding: 20px; color: ${UI_COLORS.neutral};">Preparing preview...</div>`
										}
										className="preview-iframe"
										sandbox="allow-scripts"
									/>
								</div>
							)}
						</div>
					</div>
				)}
			</div>
		</div>
	)
}
